import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import logo from "./avantica_pumps_logo-1.png";

const quotationItems = [
  {
    path: "/quotations",
    label: "Quotation List",
    icon: "fas fa-file-invoice",
    desc: "Search, filter and manage customer quotations",
    color: "from-blue-500 to-indigo-600"
  },
  {
    path: "/maker",
    label: "Quotation Maker",
    icon: "fas fa-plus-circle",
    desc: "Create and configure a new pump quotation",
    color: "from-purple-500 to-indigo-600"
  },
  {
    path: "/followups",
    label: "Follow-Ups",
    icon: "fas fa-bell",
    desc: "Track pending customer leads and status",
    color: "from-amber-500 to-orange-600"
  },
];

const toolItems = [
  {
    path: "/price-check",
    label: "Price Check",
    icon: "fas fa-tags",
    desc: "Quickly look up model rates and pricing",
    color: "from-emerald-500 to-teal-600"
  },
  {
    path: "/hsn-manager",
    label: "HSN Manager",
    icon: "fas fa-barcode",
    desc: "Manage product HSN codes and GST rates",
    color: "from-cyan-500 to-blue-600"
  },
  {
    path: "/whatsapp",
    label: "WhatsApp",
    icon: "fab fa-whatsapp",
    desc: "Send alerts and connect with customers",
    color: "from-emerald-500 to-green-600"
  },
  {
    path: "/price-change",
    label: "Price Change",
    icon: "fas fa-edit",
    desc: "Bulk update catalog pricing",
    adminOnly: true,
    color: "from-rose-500 to-pink-600"
  },
];

export default function Navbar({ onLogout, user }) {
  const navigate = useNavigate();
  const location = useLocation();
  const navRef = useRef(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null); // 'quotations' | 'tools' | null
  const closeTimeoutRef = useRef(null);
  const resizeTimer = useRef(null);
  const mutationObserverRef = useRef(null);

  const handleLogout = () => {
    if (onLogout) onLogout();
    navigate("/login");
  };

  const isActive = (path) => location.pathname === path;
  const isQuotationActive = quotationItems.some(i => location.pathname === i.path);
  const isToolActive = toolItems.some(i => location.pathname === i.path);

  const handleMouseEnter = (name) => {
    if (closeTimeoutRef.current) clearTimeout(closeTimeoutRef.current);
    setActiveDropdown(name);
  };

  const handleMouseLeave = () => {
    closeTimeoutRef.current = setTimeout(() => {
      setActiveDropdown(null);
    }, 150);
  };

  const toggleDropdown = (name) => {
    setActiveDropdown(prev => prev === name ? null : name);
  };

  const updateNavHeight = () => {
    if (!navRef.current) return;
    const h = Math.ceil(navRef.current.getBoundingClientRect().height);
    document.documentElement.style.setProperty("--nav-height", `${h}px`);
    document.body.style.paddingTop = `${h}px`;
  };

  useEffect(() => {
    updateNavHeight();

    const onScroll = () => {
      setScrolled(window.scrollY > 20);
    };

    const onResize = () => {
      clearTimeout(resizeTimer.current);
      resizeTimer.current = setTimeout(updateNavHeight, 80);
    };

    const handleClickOutside = (e) => {
      if (navRef.current && !navRef.current.contains(e.target)) {
        setActiveDropdown(null);
      }
    };

    window.addEventListener("resize", onResize);
    window.addEventListener("scroll", onScroll);
    document.addEventListener("mousedown", handleClickOutside);

    if (navRef.current && "MutationObserver" in window) {
      mutationObserverRef.current = new MutationObserver(() => {
        clearTimeout(resizeTimer.current);
        resizeTimer.current = setTimeout(updateNavHeight, 40);
      });
      mutationObserverRef.current.observe(navRef.current, {
        subtree: true,
        childList: true,
        attributes: true,
      });
    }

    return () => {
      window.removeEventListener("resize", onResize);
      window.removeEventListener("scroll", onScroll);
      document.removeEventListener("mousedown", handleClickOutside);
      if (mutationObserverRef.current) mutationObserverRef.current.disconnect();
      clearTimeout(resizeTimer.current);
      if (closeTimeoutRef.current) clearTimeout(closeTimeoutRef.current);
    };
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setActiveDropdown(null);
  }, [location.pathname]);

  return (
    <>
      <header
        ref={navRef}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled || mobileOpen ? "bg-[#1e1b4b]/95 backdrop-blur-md shadow-lg border-b border-white/10" : "bg-[#1e1b4b] border-b border-transparent"
        }`}
        style={{
          background: scrolled ? "rgba(30, 27, 75, 0.95)" : "linear-gradient(90deg, #1e1b4b 0%, #312e81 100%)",
        }}
        aria-label="Main navigation"
      >
        <div className="max-w-[1920px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            
            {/* 1. Brand Logo */}
            <div className="flex-shrink-0">
              <Link to="/" className="flex items-center gap-2.5 group">
                <div className="bg-white/10 p-1.5 rounded-xl backdrop-blur-sm border border-white/10 transition-transform group-hover:scale-105 shadow-md">
                  {logo ? (
                    <img src={logo} alt="Logo" className="h-8 w-8 object-contain drop-shadow-md" />
                  ) : (
                    <div className="h-8 w-8 flex items-center justify-center font-bold text-white bg-gradient-to-br from-purple-500 to-indigo-600 rounded-lg text-xs">AP</div>
                  )}
                </div>
                <div className="flex flex-col">
                  <span className="text-white text-sm sm:text-base font-bold tracking-tight leading-none group-hover:text-purple-300 transition-colors whitespace-nowrap">
                    Avantica Pumps
                  </span>
                  <span className="text-purple-300/70 text-[9px] font-semibold tracking-wider uppercase leading-none mt-1 whitespace-nowrap">
                    Private Limited
                  </span>
                </div>
              </Link>
            </div>

            {/* 2. Streamlined Desktop Navigation Bar (Uncluttered) */}
            <nav className="hidden md:flex items-center gap-2">
              <div className="flex items-center bg-white/5 rounded-2xl p-1 border border-white/10 backdrop-blur-md shadow-inner">
                
                {/* 2.1 Dashboard Link */}
                <Link
                  to="/"
                  className={`px-3.5 py-1.5 rounded-xl text-xs font-semibold transition-all duration-200 flex items-center gap-2 whitespace-nowrap ${
                    isActive("/")
                      ? "text-white bg-gradient-to-r from-purple-600 to-indigo-600 shadow-md shadow-purple-900/30 font-bold"
                      : "text-gray-300 hover:text-white hover:bg-white/10"
                  }`}
                >
                  <i className={`fas fa-chart-pie text-xs ${isActive("/") ? "text-white" : "text-gray-400"}`}></i>
                  Dashboard
                </Link>

                {/* 2.2 Quotations Dropdown */}
                <div 
                  className="relative"
                  onMouseEnter={() => handleMouseEnter('quotations')}
                  onMouseLeave={handleMouseLeave}
                >
                  <button
                    type="button"
                    onClick={() => toggleDropdown('quotations')}
                    className={`px-3.5 py-1.5 rounded-xl text-xs font-semibold transition-all duration-200 flex items-center gap-1.5 whitespace-nowrap cursor-pointer ${
                      isQuotationActive
                        ? "text-white bg-gradient-to-r from-purple-600 to-indigo-600 shadow-md shadow-purple-900/30 font-bold"
                        : "text-gray-300 hover:text-white hover:bg-white/10"
                    }`}
                  >
                    <i className={`fas fa-file-invoice text-xs ${isQuotationActive ? "text-white" : "text-gray-400"}`}></i>
                    Quotations
                    <i className={`fas fa-chevron-down text-[9px] transition-transform duration-200 ml-0.5 ${activeDropdown === 'quotations' ? 'rotate-180' : ''}`}></i>
                  </button>

                  {/* Dropdown Menu */}
                  {activeDropdown === 'quotations' && (
                    <div 
                      className="absolute left-0 top-full mt-2 w-72 bg-[#1e1b4b]/95 backdrop-blur-xl border border-white/15 rounded-2xl shadow-2xl p-2 z-50 animate-in fade-in slide-in-from-top-2 duration-150"
                    >
                      <div className="px-3 py-1.5 text-[10px] uppercase font-bold tracking-wider text-purple-300/60">
                        Quotation Management
                      </div>
                      <div className="space-y-1">
                        {quotationItems.map((item) => (
                          <Link
                            key={item.path}
                            to={item.path}
                            className={`flex items-start gap-3 p-2.5 rounded-xl transition-all ${
                              isActive(item.path)
                                ? "bg-white/15 text-white"
                                : "text-gray-300 hover:text-white hover:bg-white/10"
                            }`}
                          >
                            <div className={`w-8 h-8 rounded-lg bg-gradient-to-br ${item.color} flex items-center justify-center text-white text-xs shadow-md flex-shrink-0 mt-0.5`}>
                              <i className={item.icon}></i>
                            </div>
                            <div className="flex flex-col">
                              <span className="text-xs font-bold leading-tight">{item.label}</span>
                              <span className="text-[10px] text-gray-400 leading-snug mt-0.5">{item.desc}</span>
                            </div>
                          </Link>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* 2.3 Attendance Link */}
                <Link
                  to="/attendance"
                  className={`px-3.5 py-1.5 rounded-xl text-xs font-semibold transition-all duration-200 flex items-center gap-2 whitespace-nowrap ${
                    isActive("/attendance")
                      ? "text-white bg-gradient-to-r from-purple-600 to-indigo-600 shadow-md shadow-purple-900/30 font-bold"
                      : "text-gray-300 hover:text-white hover:bg-white/10"
                  }`}
                >
                  <i className={`fas fa-clock text-xs ${isActive("/attendance") ? "text-white" : "text-gray-400"}`}></i>
                  Attendance
                </Link>

                {/* 2.3b Live Attendance Link (admins only) */}
                {user?.role === "admin" && (
                  <Link
                    to="/live-attendance"
                    className={`px-3.5 py-1.5 rounded-xl text-xs font-semibold transition-all duration-200 flex items-center gap-2 whitespace-nowrap ${
                      isActive("/live-attendance")
                        ? "text-white bg-gradient-to-r from-emerald-600 to-teal-600 shadow-md shadow-emerald-900/30 font-bold"
                        : "text-gray-300 hover:text-white hover:bg-white/10"
                    }`}
                  >
                    <i className={`fas fa-fingerprint text-xs ${isActive("/live-attendance") ? "text-white" : "text-emerald-400"}`}></i>
                    Live Attendance
                  </Link>
                )}

                {/* 2.4 Tools Dropdown */}
                <div 
                  className="relative"
                  onMouseEnter={() => handleMouseEnter('tools')}
                  onMouseLeave={handleMouseLeave}
                >
                  <button
                    type="button"
                    onClick={() => toggleDropdown('tools')}
                    className={`px-3.5 py-1.5 rounded-xl text-xs font-semibold transition-all duration-200 flex items-center gap-1.5 whitespace-nowrap cursor-pointer ${
                      isToolActive
                        ? "text-white bg-gradient-to-r from-purple-600 to-indigo-600 shadow-md shadow-purple-900/30 font-bold"
                        : "text-gray-300 hover:text-white hover:bg-white/10"
                    }`}
                  >
                    <i className={`fas fa-layer-group text-xs ${isToolActive ? "text-white" : "text-gray-400"}`}></i>
                    Tools
                    <i className={`fas fa-chevron-down text-[9px] transition-transform duration-200 ml-0.5 ${activeDropdown === 'tools' ? 'rotate-180' : ''}`}></i>
                  </button>

                  {/* Dropdown Menu */}
                  {activeDropdown === 'tools' && (
                    <div 
                      className="absolute right-0 top-full mt-2 w-72 bg-[#1e1b4b]/95 backdrop-blur-xl border border-white/15 rounded-2xl shadow-2xl p-2 z-50 animate-in fade-in slide-in-from-top-2 duration-150"
                    >
                      <div className="px-3 py-1.5 text-[10px] uppercase font-bold tracking-wider text-purple-300/60">
                        System Tools & Catalog
                      </div>
                      <div className="space-y-1">
                        {toolItems.map((item) => {
                          if (item.adminOnly && user?.role !== "admin") return null;
                          return (
                            <Link
                              key={item.path}
                              to={item.path}
                              className={`flex items-start gap-3 p-2.5 rounded-xl transition-all ${
                                isActive(item.path)
                                  ? "bg-white/15 text-white"
                                  : "text-gray-300 hover:text-white hover:bg-white/10"
                              }`}
                            >
                              <div className={`w-8 h-8 rounded-lg bg-gradient-to-br ${item.color} flex items-center justify-center text-white text-xs shadow-md flex-shrink-0 mt-0.5`}>
                                <i className={item.icon}></i>
                              </div>
                              <div className="flex flex-col">
                                <span className="text-xs font-bold leading-tight">{item.label}</span>
                                <span className="text-[10px] text-gray-400 leading-snug mt-0.5">{item.desc}</span>
                              </div>
                            </Link>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* 2.5 Primary "+ New Quote" CTA Button */}
              <Link
                to="/maker"
                className="hidden xl:flex items-center gap-1.5 px-3.5 py-1.5 rounded-xl text-xs font-bold text-white bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 shadow-md shadow-blue-900/30 transition-all transform hover:-translate-y-0.5 whitespace-nowrap"
              >
                <i className="fas fa-plus text-[10px]"></i>
                New Quotation
              </Link>
            </nav>

            {/* 3. User Profile & Logout */}
            <div className="flex items-center gap-3">
              {user && (
                <div className="hidden sm:flex items-center gap-2.5 pl-3 border-l border-white/15">
                  <div className="text-right">
                    <div className="text-white text-xs font-bold tracking-wide leading-none">{user.name || "User"}</div>
                    <div className="text-[9px] uppercase font-bold tracking-wider text-purple-300 leading-none mt-0.5">{user.role || "Staff"}</div>
                  </div>
                  <div className="h-8 w-8 rounded-full bg-gradient-to-br from-purple-500 to-indigo-600 border-2 border-white/20 shadow-inner flex items-center justify-center text-white font-bold text-xs">
                    {user.name ? user.name.charAt(0).toUpperCase() : "U"}
                  </div>
                </div>
              )}

              {/* Logout Button */}
              <button
                onClick={handleLogout}
                className="hidden sm:flex items-center gap-1.5 px-3.5 py-1.5 rounded-xl bg-white/10 hover:bg-red-500/20 hover:border-red-500/50 border border-white/10 text-white text-xs font-semibold transition-all group cursor-pointer"
                title="Logout"
              >
                <i className="fas fa-power-off text-white/70 group-hover:text-red-400 transition-colors text-xs"></i>
                <span className="group-hover:text-red-100">Logout</span>
              </button>

              {/* Mobile Menu Toggle Button */}
              <div className="md:hidden">
                <button
                  onClick={() => setMobileOpen((s) => !s)}
                  className={`p-2 rounded-xl transition-all duration-300 cursor-pointer ${
                    mobileOpen ? "bg-white/10 text-white rotate-90" : "text-white/80 hover:text-white hover:bg-white/10"
                  }`}
                  aria-label="Toggle navigation menu"
                >
                  <i className={`fas ${mobileOpen ? "fa-times" : "fa-bars"} text-xl`}></i>
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* 4. Mobile Drawer Navigation */}
        <div
          className={`md:hidden fixed inset-x-0 top-[64px] bg-[#0f172a]/98 backdrop-blur-2xl border-b border-white/10 transition-all duration-300 overflow-y-auto max-h-[85vh] z-40 ${
            mobileOpen ? "opacity-100 visible shadow-2xl" : "opacity-0 invisible pointer-events-none"
          }`}
        >
          <div className="p-4 space-y-4 max-w-lg mx-auto">
            {/* Primary Section */}
            <div className="space-y-1">
              <div className="px-3 text-[10px] uppercase font-bold tracking-wider text-purple-300/60">
                Core
              </div>
              <Link
                to="/"
                onClick={() => setMobileOpen(false)}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-semibold transition-all ${
                  isActive("/") ? "bg-purple-600/30 text-purple-200 border border-purple-500/30" : "text-gray-300 hover:bg-white/5"
                }`}
              >
                <i className="fas fa-chart-pie w-5 text-center text-purple-400"></i>
                Dashboard
              </Link>
              <Link
                to="/attendance"
                onClick={() => setMobileOpen(false)}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-semibold transition-all ${
                  isActive("/attendance") ? "bg-purple-600/30 text-purple-200 border border-purple-500/30" : "text-gray-300 hover:bg-white/5"
                }`}
              >
                <i className="fas fa-clock w-5 text-center text-indigo-400"></i>
                Biometric Attendance
              </Link>
              {user?.role === "admin" && (
                <Link
                  to="/live-attendance"
                  onClick={() => setMobileOpen(false)}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-semibold transition-all ${
                    isActive("/live-attendance") ? "bg-emerald-600/30 text-emerald-200 border border-emerald-500/30" : "text-gray-300 hover:bg-white/5"
                  }`}
                >
                  <i className="fas fa-fingerprint w-5 text-center text-emerald-400"></i>
                  Live Attendance
                </Link>
              )}
            </div>

            {/* Quotations Section */}
            <div className="space-y-1 pt-2 border-t border-white/10">
              <div className="px-3 text-[10px] uppercase font-bold tracking-wider text-purple-300/60">
                Quotations
              </div>
              {quotationItems.map((it) => (
                <Link
                  key={it.path}
                  to={it.path}
                  onClick={() => setMobileOpen(false)}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-semibold transition-all ${
                    isActive(it.path) ? "bg-purple-600/30 text-purple-200 border border-purple-500/30" : "text-gray-300 hover:bg-white/5"
                  }`}
                >
                  <i className={`${it.icon} w-5 text-center text-blue-400`}></i>
                  {it.label}
                </Link>
              ))}
            </div>

            {/* Tools Section */}
            <div className="space-y-1 pt-2 border-t border-white/10">
              <div className="px-3 text-[10px] uppercase font-bold tracking-wider text-purple-300/60">
                Tools & Settings
              </div>
              {toolItems.map((it) => {
                if (it.adminOnly && user?.role !== "admin") return null;
                return (
                  <Link
                    key={it.path}
                    to={it.path}
                    onClick={() => setMobileOpen(false)}
                    className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-semibold transition-all ${
                      isActive(it.path) ? "bg-purple-600/30 text-purple-200 border border-purple-500/30" : "text-gray-300 hover:bg-white/5"
                    }`}
                  >
                    <i className={`${it.icon} w-5 text-center text-emerald-400`}></i>
                    {it.label}
                  </Link>
                );
              })}
            </div>

            {/* User Info & Logout */}
            <div className="border-t border-white/10 pt-3">
              {user && (
                <div className="flex items-center gap-3 mb-3 p-2 bg-white/5 rounded-xl">
                  <div className="h-9 w-9 rounded-full bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center text-white font-bold text-xs">
                    {user?.name ? user.name.charAt(0).toUpperCase() : "U"}
                  </div>
                  <div>
                    <div className="text-white text-xs font-bold">{user?.name}</div>
                    <div className="text-[10px] text-purple-300 uppercase">{user?.role}</div>
                  </div>
                </div>
              )}
              <button
                onClick={() => {
                  setMobileOpen(false);
                  handleLogout();
                }}
                className="w-full flex items-center justify-center gap-2 bg-red-500/15 hover:bg-red-500/25 text-red-200 border border-red-500/30 py-2.5 rounded-xl text-xs font-bold transition-all cursor-pointer"
              >
                <i className="fas fa-power-off text-xs"></i>
                Logout
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Global CSS Styling */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@300;400;500;600;700&display=swap');
        
        body { 
            font-family: 'Space Grotesk', sans-serif; 
            margin: 0; 
            background-color: #0f172a; 
            background-image: 
                radial-gradient(at 0% 0%, hsla(253,16%,7%,1) 0, transparent 50%), 
                radial-gradient(at 50% 0%, hsla(225,39%,30%,1) 0, transparent 50%), 
                radial-gradient(at 100% 0%, hsla(339,49%,30%,1) 0, transparent 50%);
            min-height: 100vh;
        }
        
        /* Custom Scrollbar for Webkit */
        ::-webkit-scrollbar {
          width: 8px;
        }
        ::-webkit-scrollbar-track {
          background: #0f172a; 
        }
        ::-webkit-scrollbar-thumb {
          background: #334155; 
          border-radius: 4px;
        }
        ::-webkit-scrollbar-thumb:hover {
          background: #475569; 
        }
      `}</style>
    </>
  );
}
